import {
  TpBitmap,
  TpCanonicalTypeInfo,
  TpDataContext,
  TpDataTable,
  TpDataTableColumn,
  TpDataTableGroup,
  TpDendrogram,
} from '../../../data/interfaces';

interface TpDataContextSchemaProps {
  dataContext: TpDataContext;
}

function renderIdentifier(identifierCode: string, identifier: string) {
  return (
    <output>
      <mark>{identifierCode}</mark> <code>{identifier}</code>
    </output>
  );
}

function DataContextSchema({ dataContext }: TpDataContextSchemaProps) {
  function renderCT(ct: TpCanonicalTypeInfo) {
    return (
      <div key={ct.id}>
        <h2>{ct.name}</h2>
        {renderIdentifier('ID', ct.id)}
      </div>
    );
  }

  function renderColumn(table: TpDataTable, column: TpDataTableColumn) {
    return (
      <div key={column.id} style={{ margin: '10px 0' }}>
        <div>
          <b>{column.name}</b>
        </div>
        <div>
          {renderIdentifier('ID', column.id)}
          {renderIdentifier('TP', column.valueType)}
          {column.columnCanonicalTypeId && renderIdentifier('CT', column.columnCanonicalTypeId)}
          {column.columnItemId && renderIdentifier('ITM', column.columnItemId)}
        </div>
      </div>
    );
  }

  function renderColumnGroup(table: TpDataTable, columnGroup: TpDataTableGroup) {
    return (
      <div key={columnGroup.id}>
        <h3>{columnGroup.name}</h3>
        {renderIdentifier('ID', columnGroup.id)}
        {table.columns.filter((col) => col.groupId == columnGroup.id).map((col) => renderColumn(table, col))}
      </div>
    );
  }

  function renderTable(table: TpDataTable) {
    return (
      <div key={table.id}>
        <h2>{table.name}</h2>
        {renderIdentifier('ID', table.id)}
        {renderIdentifier('CT', table.canonicalTypeId)}
        {renderIdentifier('ITEMID', table.colItemId)}
        {renderIdentifier('LABELID', table.colLabel)}
        {table.defaultHoverLabel && renderIdentifier('HOVER', table.defaultHoverLabel)}
        {table.columns.filter((col) => !col.groupId).map((col) => renderColumn(table, col))}
        {table.columnGroups.map((columnGroup) => renderColumnGroup(table, columnGroup))}
      </div>
    );
  }

  function renderDendrogram(dnd: TpDendrogram) {
    return (
      <div key={dnd.id}>
        <h2>{dnd.name}</h2>
        {renderIdentifier('ID', dnd.id)}
        {renderIdentifier('CT', dnd.canonicalTypeId)}
      </div>
    );
  }

  function renderBitmap(bitmap: TpBitmap) {
    const tags = bitmap.tags.join(',');
    return (
      <div key={bitmap.id}>
        <h2>{bitmap.name}</h2>
        {renderIdentifier('ID', bitmap.id)}
        {tags && renderIdentifier('TAGS', tags)}
      </div>
    );
  }

  return (
    <div
      style={{
        height: '100%',
        overflow: 'auto',
      }}
    >
      <div
        style={{
          margin: '15px',
        }}
      >
        <h1>{dataContext.name}</h1>
        {renderIdentifier('ID', dataContext.id)}
        {renderIdentifier('ASSET', dataContext.assetId)}
        <h1>Canonical Types</h1>
        {Object.keys(dataContext.canonicalTypes).map((ctId) => renderCT(dataContext.canonicalTypes[ctId]))}
        <h1>Data tables</h1>
        {dataContext.dataTables.map((table) => renderTable(table))}
        {dataContext.dendrograms.length > 0 && (
          <>
            <h1>Dendrograms</h1>
            {dataContext.dendrograms.map((dnd) => renderDendrogram(dnd))}
          </>
        )}
        {Object.keys(dataContext.bitmaps).length > 0 && (
          <>
            <h1>Bitmaps</h1>
            {Object.keys(dataContext.bitmaps).map((bitmapId) => renderBitmap(dataContext.bitmaps[bitmapId]))}
          </>
        )}
      </div>
    </div>
  );
}

export default DataContextSchema;
